import {
  useRefinementList,
  type RefinementListProps,
} from "react-instantsearch";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { CheckboxInput, Label } from "@tudigo-monorepo/web-tudigo-components";

import { useTranslateRefinementItemLabel } from "../../hooks/use-translate-refinement-item-label";
import {
  algoliaRefinementValueToKey,
  type AlgoliaRefinementAttributesValue,
  type RefinementAttributeList,
} from "../../models/algolia-refinement-attributes";
import type { SearchProjectsRefinementsProps } from "./search-projects-refinements";

export type RefinementListItem = {
  attribute: AlgoliaRefinementAttributesValue;
  count: number;
  label: string;
  value: string;
};

interface CustomRefinementListProps extends RefinementListProps {
  refinementList: RefinementAttributeList;
  onRefineItemChange: SearchProjectsRefinementsProps["onRefineItemChange"];
}

export function CustomRefinementList(props: CustomRefinementListProps) {
  const { items, refine } = useRefinementList({
    attribute: props.attribute,
  });
  const translateItemLabel = useTranslateRefinementItemLabel();

  const handleCheckedChange = (
    item: Omit<RefinementListItem, "attribute">,
    checked: boolean,
  ) => {
    refine(item.value);
    props.onRefineItemChange?.(
      props.refinementList,
      item as RefinementListItem,
      checked,
    );
  };

  return (
    <ul
      style={props.style}
      className={cn("flex w-full flex-col gap-y-1 bg-white", props.className)}
    >
      {items.map((item) => (
        <li key={item.value}>
          <Label
            htmlFor={item.label}
            role="button"
            className="flex h-fit w-full flex-1 cursor-pointer items-center rounded p-2"
          >
            <CheckboxInput
              id={item.label}
              className="mr-2"
              checked={item.isRefined}
              onCheckedChange={(checked) =>
                handleCheckedChange(item, !!checked)
              }
            />
            <div className="flex w-full items-center gap-x-2 font-sans">
              <span className="text-dark-1 whitespace-nowrap text-[13px] capitalize">
                {translateItemLabel(
                  algoliaRefinementValueToKey[
                    props.attribute as AlgoliaRefinementAttributesValue
                  ],
                  item,
                )}
              </span>
              <span className="bg-light-3 inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-1 text-[10px]">
                {item.count}
              </span>
            </div>
          </Label>
        </li>
      ))}
    </ul>
  );
}
